import { Fragment, useEffect, useLayoutEffect, useMemo, useRef, useState, type ClipboardEvent, type DragEvent, type FormEvent, type KeyboardEvent, type ReactNode } from "react";
import { textParts } from "../../oeffentlich/marke-model";
import { Link } from "react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../../../data";
import { Dialog, PillView, useAction } from "../../../components/ui";
import { Icon, type IconName } from "../../../components/Icon";
import { describeError } from "../../../components/States";
import { toast } from "../../../components/Toast";
import { date, initials, time, today as todayIn } from "../../../lib/format";
import { useDocumentVisible } from "../../../lib/sichtbarkeit";
import { useLang } from "../../creator/sprache";
import { dateOf } from "../../creator/lang";
import {
  addNote, attachFiles, fetchAttachment, findSentMessage, loadChatContacts, loadTalentReach, markThreadRead, missingFiles, retractMessage, saveTalentMailPrefs, sendMessage,
  toggleReaction, type ChatContact, type TalentReach,
} from "./data";
import { useLinkObjects, useMentionable, useThread } from "./hooks";
import {
  EMOJIS, EMPTY_LINKS, fileHint, FILE_ACCEPT, MAX_BODY, MAX_FILES, PAGE_SIZE, RETRACT_HOURS, ZUSTELL_ZEICHEN, canRetract, checkFile, deliveryLabel, fileKindLabel,
  fileOnlyBody, fileSizeLabel, firstUnreadIncoming, gruppenLage, highlightParts, insertMention, isImage, isPreviewable, linkCard, linkTabs, mentionParts, mentionQuery,
  mentionSuggestions, messageBody, parseLinkValue, parseMentions, quoteText, reactionChips, searchHits, shouldMarkRead, threadContext, threadItems, zustellung,
  type AttachmentRow, type GruppenLage, type LinkCard, type LinkKind, type LinkObjects, type Mentionable, type MessageRow, type NoteRow, type Portal, type ReactionChip,
  type Side,
} from "./model";
import { verkleinereBild } from "../../../lib/bildverkleinern";
import "./nachrichten.css";
import { t } from "../../../lib/sprache";

/** Beim Suchen und beim Sprung zu einem älteren Zitat lädt der Verlauf bis zu so viele Nachrichten. */
const SEARCH_LIMIT = 2000;

/**
 * Verlauf mit Eingabe (11_app_agentur/chat.html, 12_app_creator/chat.html): Blasen mit Absender, Zeit und Zustellung,
 * Objekt-Karten, Dateien, Reaktionen, Tagestrenner, „Neue Nachrichten“, Senden mit Wiederholen (AD-180 … AD-184,
 * TA-110 … TA-115). Beide Portale nutzen denselben Baustein; die Seite legt nur fest, wer „ich“ bin.
 * K-525: Suche im Verlauf, Antworten mit Zitat, interne Notizen mit @-Erwähnung (nur Team), Office-Dateien als Download.
 * K-524: kompakte Fassung für den Mini-Chat. K-529 d: Bilder werden vor dem Hochladen verkleinert.
 * D-1 S6 (Inhaber 21.10.2026): Messenger-Aufbau. Kopf 56 px mit Unterzeile und ⋯-Menü (Zustellung), nur der Verlauf scrollt,
 * Folgenachrichten gruppiert, Zeit und Zustellung in der Blase, Aktionen als Mini-Leiste bei Hover/Fokus (am Handy Langdruck
 * oder ⋯), Eingabe einzeilig und mitwachsend mit „+“-Menü und rundem Senden-Knopf.
 */

export interface ThreadPanelProps {
  portal: Portal;
  orgId: string;
  talentId: string;
  /** Überschrift des Verlaufs: Creator (Agentur) bzw. Agentur (Creator) */
  title: string;
  /** Überschrift als Link (Agentur: zum Creator-Profil) */
  titleHref?: string;
  timeZone: string;
  /** Name des Creators für Kooperationskarten („Lunava × Mara Linden“) */
  talentName: string;
  /** Absender je Nachricht, wenn er nicht aus der Überschrift hervorgeht (Teammitglieder im Agentur-Portal) */
  senderName?: (m: MessageRow) => string | null;
  /** K-517: Objekt aus „Im Chat teilen“ (kind:id), im Eingabefeld vorbelegt */
  vorbelegt?: string | null;
  /** Hinweis über dem Verlauf (z. B. archivierter Creator) */
  notice?: ReactNode;
  /** Zurück zur Liste (nur auf dem Handy sichtbar, dort steht die Liste über dem Verlauf) */
  back?: string;
  /** K-524: kompakte Fassung im Mini-Chat (niedriger Verlauf, ohne Mail-Einstellungen); Zurück als Knopf */
  compact?: boolean;
  onBack?: () => void;
  /** Anzeigename eines Team-Mitglieds (Verfasser interner Notizen, Agentur) */
  memberName?: (userId: string | null) => string | null;
  /** Aus der Glocke: zu dieser internen Notiz springen (?notiz=<id>) */
  focusNote?: string | null;
}

interface Pending {
  key: string;
  text: string;
  link: { kind: LinkKind; id: string } | null;
  files: File[];
  replyTo: string | null;
  since: string;
  state: "sending" | "failed";
  error?: string;
}
interface PartialUpload {
  messageId: string;
  files: File[];
  errors: string[];
}
interface Reply {
  id: string;
  who: string;
  text: string;
}

let seq = 0;
const nextKey = () => `p${Date.now()}-${++seq}`;

export function ThreadPanel(p: ThreadPanelProps) {
  const lang = useLang();
  const t = (de: string, en: string) => (lang === "en" ? en : de);
  const me = api.session()?.userId ?? null;
  const mySide: Side = p.portal === "agentur" ? "agency" : "talent";
  // Kennungen im Mini-Chat mit Vorsilbe (er steht nie auf derselben Seite wie der große Verlauf, sicher ist sicher)
  const ids = p.compact ? "mini-" : "";
  // Erreichbarkeit des Creators (Agentur, 0056): Portalzugang, Adresse, Mail-Wünsche, letzter Rückläufer
  const reach = useQuery({
    queryKey: ["talent_reach", p.orgId, p.talentId, "nachrichten"], enabled: p.portal === "agentur",
    queryFn: () => loadTalentReach(api, p.orgId, p.talentId),
  });
  // Ansprechpartner mit Abwesenheit (beide Seiten)
  const contacts = useQuery({ queryKey: ["chat_contacts", p.talentId, "nachrichten"], queryFn: () => loadChatContacts(api, p.talentId) });
  const erreichbar: "portal" | "mail" | "none" | undefined = p.portal !== "agentur" || !reach.data ? undefined
    : reach.data.hasAccount ? "portal" : reach.data.email && reach.data.chatMail ? "mail" : "none";
  const today = todayIn(p.timeZone);
  const qc = useQueryClient();
  const [limit, setLimit] = useState(PAGE_SIZE);
  const thread = useThread(p.orgId, p.talentId, limit, p.portal);
  const links = useLinkObjects(p.portal, p.orgId, p.talentId);
  // K-525: wen das Team in internen Notizen erwähnen kann (nur Agentur)
  const mentionable = useMentionable(p.talentId, p.portal === "agentur");
  const objs: LinkObjects = links.data ?? EMPTY_LINKS;
  const data = thread.data && thread.data.messages.every((m) => m.talent_id === p.talentId) ? thread.data : undefined;
  const reacting = useAction();

  const [pending, setPending] = useState<Pending[]>([]);
  const [partial, setPartial] = useState<PartialUpload | null>(null);
  const [reply, setReply] = useState<Reply | null>(null);

  // „Neue Nachrichten“: beim ersten Laden festhalten, auch wenn die Nachrichten gleich darauf als gelesen gelten
  const [newFrom, setNewFrom] = useState<string | null | undefined>(undefined);
  useEffect(() => {
    if (data && newFrom === undefined) setNewFrom(firstUnreadIncoming(data.messages, mySide));
  }, [data, newFrom, mySide]);

  // Sichtbarer Verlauf gilt als gelesen (AD-181, TA-115). Einmal je neuer Nachricht, nie in einer Abfrage (Bauanleitung 3).
  // Im Hintergrund-Tab wartet das Markieren, bis der Tab wieder sichtbar ist (visibilitychange, Gesamtprüfung 07.10.2026)
  const marked = useRef<string | null>(null);
  const visible = useDocumentVisible();
  const lastUnread = data ? ([...data.messages].reverse().find((m) => m.sender_side !== mySide && !m.read_at)?.id ?? null) : null;
  useEffect(() => {
    if (!shouldMarkRead(lastUnread, marked.current, visible)) return;
    marked.current = lastUnread;
    markThreadRead(api, p.talentId, mySide)
      .then(() => qc.invalidateQueries({ predicate: (q) => ["my_chat_threads", "unread", "messages"].includes(String(q.queryKey[0])) }))
      .catch(() => {
        marked.current = null;
      });
  }, [lastUnread, p.talentId, qc, visible]);

  // Bildlauf: neue Nachricht → nach unten; ältere nachgeladen → Position halten; beim Suchen und Springen nicht nach unten
  const box = useRef<HTMLDivElement>(null);
  const amEnde = useRef(true);
  const keep = useRef<number | null>(null);
  const sprungZiel = useRef<string | null>(null);
  const [suchOffen, setSuchOffen] = useState(false);
  const [suche, setSuche] = useState("");
  const sucht = suchOffen && suche.trim().length >= 2;
  const lastId = data?.messages.at(-1)?.id ?? null;
  // K-565 a: beim Öffnen mit ungelesenen Nachrichten steht der Trenner „Neue Nachrichten“ oben im Bild (wie in Messengern),
  // sonst das Ende; danach wie bisher
  const amTrenner = useRef(false);
  useLayoutEffect(() => {
    const el = box.current;
    if (!el) return;
    const trenner = newFrom && !amTrenner.current ? el.querySelector<HTMLElement>(".tk-new") : null;
    if (keep.current !== null) {
      el.scrollTop = el.scrollHeight - keep.current;
      keep.current = null;
    } else if (trenner) {
      amTrenner.current = true;
      const oben = trenner.getBoundingClientRect().top - el.getBoundingClientRect().top + el.scrollTop - 8;
      // passt alles Neue ins Bild, bleibt es beim Ende; sonst beginnt der Blick am Trenner
      el.scrollTop = Math.min(el.scrollHeight - el.clientHeight, Math.max(0, oben));
      amEnde.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
    } else if (!sprungZiel.current && !sucht) {
      // D-1 S6: nur der Verlauf scrollt (auch am Handy), die Seite steht; also nur ihn ans Ende setzen
      el.scrollTop = el.scrollHeight;
      amEnde.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastId, data?.messages.length, data?.notes.length, pending.length, newFrom]);

  // Bilder laden nach dem ersten Zeichnen: stand der Verlauf unten, bleibt er unten (load-Ereignisse im Einfangen). Ob er
  // unten steht, zählt nur nach eigenem Scrollen der Person (Rad, Wischen, Tasten), nicht nach dem Wachsen durch ein Bild.
  useEffect(() => {
    const el = box.current;
    if (!el) return;
    let nutzer = 0;
    const handlung = () => { nutzer = Date.now(); };
    const scroll = () => { if (Date.now() - nutzer < 1200) amEnde.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80; };
    const geladen = (e: Event) => {
      if ((e.target as HTMLElement).tagName === "IMG" && amEnde.current && !sprungZiel.current) el.scrollTop = el.scrollHeight;
    };
    // Wird der Verlauf niedriger (Zitat, Dateien, Notizhinweis über dem Feld, wachsendes Feld), bleibt das Ende im Blick
    const groesse = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(() => {
      if (amEnde.current && !sprungZiel.current) el.scrollTop = el.scrollHeight;
    });
    groesse?.observe(el);
    const arten = ["wheel", "touchmove", "keydown", "pointerdown"] as const;
    for (const a of arten) el.addEventListener(a, handlung, { passive: true });
    el.addEventListener("scroll", scroll, { passive: true });
    el.addEventListener("load", geladen, true);
    return () => {
      groesse?.disconnect();
      for (const a of arten) el.removeEventListener(a, handlung);
      el.removeEventListener("scroll", scroll);
      el.removeEventListener("load", geladen, true);
    };
  }, []);

  const items = useMemo(
    () => (data ? threadItems(data.messages, { mySide, today, timeZone: p.timeZone, newFrom: newFrom ?? null, lang, notes: data.notes, me }) : []),
    [data, mySide, today, p.timeZone, newFrom, lang, me],
  );
  const attachmentsOf = useMemo(() => {
    const map = new Map<string, AttachmentRow[]>();
    for (const a of data?.attachments ?? []) map.set(a.owner_id, [...(map.get(a.owner_id) ?? []), a]);
    return map;
  }, [data]);
  const byId = useMemo(() => new Map([...(data?.quoted ?? []), ...(data?.messages ?? [])].map((m) => [m.id, m])), [data]);
  const context = data ? threadContext(data.messages, objs) : null;

  /** Wer hat geschrieben (für Zitat und Antwort-Vorschau): „Du“, Teammitglied, Creator bzw. Agentur */
  function wer(m: MessageRow): string {
    if (m.sender_side === mySide && (!m.sender_user_id || m.sender_user_id === me)) return t("Du", "You");
    if (m.sender_side === "talent") return p.portal === "agentur" ? p.talentName : t("Du", "You");
    return p.senderName?.(m) ?? (p.portal === "agentur" ? t("Team", "Team") : p.title);
  }

  // ── K-525: Suche im Verlauf ──
  const [trefferNr, setTrefferNr] = useState(0);
  const suchFeld = useRef<HTMLInputElement>(null);
  const hits = useMemo(() => (suchOffen ? searchHits(items, suche, (id) => (attachmentsOf.get(id) ?? []).map((a) => a.file_name)) : []), [items, suche, suchOffen, attachmentsOf]);
  const aktiv = hits.length ? hits[Math.min(trefferNr, hits.length - 1)]! : null;
  // Gesucht wird im ganzen Faden: ältere Nachrichten dafür nachladen (bis SEARCH_LIMIT)
  useEffect(() => {
    if (sucht && data?.hasMore && limit < SEARCH_LIMIT) setLimit(SEARCH_LIMIT);
  }, [sucht, data?.hasMore, limit]);
  // Neue Suche beginnt beim neuesten Treffer (unten), wie man im Verlauf liest
  useEffect(() => {
    setTrefferNr(Math.max(0, hits.length - 1));
  }, [suche, hits.length]);

  // ── Sprung zu einem Eintrag (Treffer, Zitat, Notiz aus der Glocke) ──
  const [blink, setBlink] = useState<string | null>(null);
  function zeige(key: string, markieren: boolean): boolean {
    const el = box.current;
    const ziel = el ? [...el.querySelectorAll<HTMLElement>("[data-key]")].find((x) => x.dataset.key === key) : undefined;
    if (!el || !ziel) return false;
    // Nur im Verlauf scrollen (scrollIntoView würde auch die feste Seite verschieben); danach nicht mehr ans Ende springen
    amEnde.current = false;
    if (el.scrollHeight > el.clientHeight + 1) el.scrollTop = Math.max(0, ziel.offsetTop - el.clientHeight / 2 + ziel.clientHeight / 2);
    if (markieren) {
      setBlink(key);
      window.setTimeout(() => setBlink((b) => (b === key ? null : b)), 1600);
    }
    return true;
  }
  function springeZu(key: string) {
    if (zeige(key, true)) return;
    if (data?.hasMore) {
      sprungZiel.current = key;
      setLimit(SEARCH_LIMIT);
    } else toast(t("Diese Nachricht ist nicht mehr im Verlauf.", "This message is no longer in the conversation."));
  }
  useEffect(() => {
    if (aktiv) zeige(aktiv, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aktiv, items.length]);
  useEffect(() => {
    const key = sprungZiel.current;
    if (!key || !data) return;
    if (zeige(key, true) || !data.hasMore) sprungZiel.current = null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);
  // Aus der Glocke (Erwähnung): einmal zur Notiz springen, sobald sie geladen ist
  const fokusErledigt = useRef(false);
  useEffect(() => {
    if (!p.focusNote || fokusErledigt.current || !data) return;
    const key = `note:${p.focusNote}`;
    if (items.some((it) => it.key === key)) {
      fokusErledigt.current = true;
      sprungZiel.current = key;
      window.setTimeout(() => {
        zeige(key, true);
        sprungZiel.current = null;
      }, 60);
    } else if (data.hasMore && limit < SEARCH_LIMIT) setLimit(SEARCH_LIMIT);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [p.focusNote, data, items]);

  async function refetchThread() {
    await Promise.all([
      qc.refetchQueries({ queryKey: ["messages", p.orgId, p.talentId] }),
      qc.invalidateQueries({ predicate: (q) => ["my_chat_threads", "unread", "my_notifications"].includes(String(q.queryKey[0])) }),
    ]);
  }

  async function deliver(item: Pending) {
    setPending((list) => list.map((x) => (x.key === item.key ? { ...x, state: "sending", error: undefined } : x)));
    try {
      const res = await sendMessage(api, { orgId: p.orgId, talentId: p.talentId, side: mySide, body: item.text, link: item.link, files: item.files, replyTo: item.replyTo });
      await refetchThread();
      setPending((list) => list.filter((x) => x.key !== item.key));
      if (res.failed.length) setPartial({ messageId: res.messageId, files: res.failed.map((f) => f.file), errors: res.failed.map((f) => `${f.file.name}: ${f.error}`) });
    } catch (e) {
      const d = describeError(e);
      setPending((list) => list.map((x) => (x.key === item.key ? { ...x, state: "failed", error: `${d.title} ${d.text}`.trim() } : x)));
    }
  }

  /** Erneut senden, aber nicht doppelt: kam die Nachricht beim ersten Versuch doch an, nur fehlende Dateien nachreichen. */
  async function retry(item: Pending) {
    setPending((list) => list.map((x) => (x.key === item.key ? { ...x, state: "sending", error: undefined } : x)));
    const body = messageBody(item.text, item.files.map((f) => f.name));
    const sent = me ? await findSentMessage(api, { orgId: p.orgId, talentId: p.talentId, userId: me, body, since: item.since }).catch(() => null) : null;
    if (!sent) return deliver(item);
    const missing = await missingFiles(api, sent, item.files).catch(() => item.files);
    const failed = missing.length ? await attachFiles(api, p.orgId, sent, missing) : [];
    await refetchThread();
    setPending((list) => list.filter((x) => x.key !== item.key));
    if (failed.length) setPartial({ messageId: sent, files: failed.map((f) => f.file), errors: failed.map((f) => `${f.file.name}: ${f.error}`) });
  }

  function queue(text: string, files: File[], link: Pending["link"]) {
    const item: Pending = { key: nextKey(), text, link, files, replyTo: reply?.id ?? null, since: new Date(Date.now() - 120_000).toISOString(), state: "sending" };
    setPending((list) => [...list, item]);
    setReply(null);
    void deliver(item);
  }

  /** Interne Notiz speichern (nur Team); wirft bei Fehler, damit der Text im Eingabefeld bleibt. */
  async function saveNote(text: string, mentions: string[]) {
    await addNote(api, p.talentId, text, mentions);
    await refetchThread();
    const andere = mentions.filter((x) => x !== me).length;
    toast(andere === 0 ? t("Notiz gespeichert. Nur dein Team sieht sie.", "Note saved. Only your team can see it.")
      : andere === 1 ? t("Notiz gespeichert. Die erwähnte Person bekommt einen Hinweis.", "Note saved. The person mentioned gets a notice.")
      : t(`Notiz gespeichert. ${andere} erwähnte Personen bekommen einen Hinweis.`, `Note saved. ${andere} people mentioned get a notice.`));
  }

  async function retryPartial() {
    if (!partial) return;
    const failed = await attachFiles(api, p.orgId, partial.messageId, partial.files);
    await refetchThread();
    if (failed.length) {
      setPartial({ ...partial, files: failed.map((f) => f.file), errors: failed.map((f) => `${f.file.name}: ${f.error}`) });
      toast(t("Die Datei wurde nicht hochgeladen. Nachrichten nehmen nur in der ersten Stunde Dateien an.", "The file was not uploaded. Messages only accept files within the first hour."), "alert");
    } else {
      setPartial(null);
      toast(t("Datei angehängt.", "File attached."));
    }
  }

  function loadOlder() {
    const el = box.current;
    keep.current = el ? el.scrollHeight - el.scrollTop : null;
    setLimit((l) => l + PAGE_SIZE);
  }

  function schliesseSuche() {
    setSuchOffen(false);
    setSuche("");
  }
  function naechster(richtung: 1 | -1) {
    if (!hits.length) return;
    setTrefferNr((n) => (Math.min(n, hits.length - 1) + richtung + hits.length) % hits.length);
  }
  const trefferStand = suche.trim().length < 2 ? t("ab 2 Zeichen", "2+ characters")
    : !hits.length && thread.isFetching ? t("sucht …", "searching …")
    : hits.length ? t(`${Math.min(trefferNr, hits.length - 1) + 1} von ${hits.length}`, `${Math.min(trefferNr, hits.length - 1) + 1} of ${hits.length}`)
    : t("keine Treffer", "no matches");

  const lage = useMemo(() => gruppenLage(items), [items]);
  const zurueckText = t("Zu allen Unterhaltungen", "All conversations");

  return (
    <section className={`tk-faden${p.compact ? " tk-chat-kompakt" : ""}`} aria-labelledby={`${ids}verlauf-titel`}>
      {/* D-1 S6: Kopf 56 px wie im Messenger: Zurück (Handy/Mini-Chat), Kürzel, Name, Unterzeile; rechts Objekt, Suche, ⋯ */}
      <header className="tk-chat-kopf tk-faden-kopf">
        {p.back && <Link className="tk-faden-zurueck tk-back" to={p.back} aria-label={zurueckText} title={zurueckText}><Icon name="back" /></Link>}
        {p.onBack && (
          <button type="button" className="tk-faden-zurueck tk-chat-zurueck" onClick={p.onBack} aria-label={zurueckText} title={zurueckText}><Icon name="back" /></button>
        )}
        <span className="tk-chat-avatar" aria-hidden="true">{initials(p.title)}</span>
        <div className="tk-faden-titel">
          <h3 id={`${ids}verlauf-titel`}>{p.titleHref ? <Link to={p.titleHref}>{p.title}</Link> : p.title}</h3>
          <small className="tk-faden-unterzeile">
            {p.portal === "agentur"
              ? reach.data ? <Unterzeile talentId={p.talentId} name={p.talentName} reach={reach.data} archiviert={!!p.notice} /> : null
              : t("Deine Agentur", "Your agency")}
          </small>
        </div>
        <div className="tk-faden-werkzeuge">
          {context && !p.compact && <span className="tk-faden-objekt" title={context}>{context}</span>}
          <button type="button" className="tk-faden-knopf tk-chat-suche-knopf" aria-expanded={suchOffen} aria-controls={`${ids}verlauf-suche`}
            aria-label={t("Im Verlauf suchen", "Search conversation")} title={t("Im Verlauf suchen", "Search conversation")}
            onClick={() => {
              if (suchOffen) schliesseSuche();
              else {
                setSuchOffen(true);
                window.setTimeout(() => suchFeld.current?.focus(), 0);
              }
            }}>
            <Icon name="search" />
          </button>
          {p.portal === "agentur" && reach.data && (
            <FadenMenue ids={ids} talentId={p.talentId} name={p.talentName} reach={reach.data} timeZone={p.timeZone} compact={p.compact} profil={p.titleHref ?? null} />
          )}
        </div>
      </header>
      {suchOffen && (
        <div className="tk-chat-suche" id={`${ids}verlauf-suche`} role="search">
          <label className="tk-chat-suche-feld">
            <span className="sr-only">{t("Im Verlauf suchen", "Search conversation")}</span>
            <input ref={suchFeld} type="search" value={suche} placeholder={t("Im Verlauf suchen …", "Search this conversation …")}
              onChange={(e) => setSuche(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  naechster(e.shiftKey ? 1 : -1);
                }
                if (e.key === "Escape") {
                  e.preventDefault();
                  schliesseSuche();
                }
              }} />
          </label>
          <small className="tk-chat-suche-stand" aria-live="polite">{trefferStand}</small>
          <button type="button" className="tk-faden-knopf" disabled={hits.length < 2} onClick={() => naechster(-1)} aria-label={t("Vorheriger Treffer", "Previous match")}>↑</button>
          <button type="button" className="tk-faden-knopf" disabled={hits.length < 2} onClick={() => naechster(1)} aria-label={t("Nächster Treffer", "Next match")}>↓</button>
          <button type="button" className="tk-faden-knopf" onClick={schliesseSuche} aria-label={t("Suche schließen", "Close search")}>×</button>
        </div>
      )}
      {/* Hinweise als schmale Zeilen unter dem Kopf statt großer Kästen: archiviert, Rückläufer, Abwesenheit */}
      {p.notice && <div className="tk-faden-hinweis" role="status">{p.notice}</div>}
      {p.portal === "agentur" && reach.data?.lastProblem && <Ruecklaeufer talentId={p.talentId} reach={reach.data} timeZone={p.timeZone} />}
      <Abwesend contacts={contacts.data ?? []} portal={p.portal} lang={lang} />
      <div id={`${ids}messages`} className="tk-messages" ref={box} role="log" aria-live="polite" aria-label={t(`Verlauf mit ${p.title}`, `Conversation with ${p.title}`)} aria-busy={thread.isFetching}>
        {thread.isPending && <p className="muted tk-verlauf-stand">{t("Verlauf wird geladen …", "Loading conversation …")}</p>}
        {thread.error && (
          <div className="stack tk-verlauf-stand" role="alert">
            <p className="tk-error">{describeError(thread.error).title} {describeError(thread.error).text}</p>
            <div className="row"><button type="button" onClick={() => void thread.refetch()}>{t("Erneut versuchen", "Try again")}</button></div>
          </div>
        )}
        {data?.hasMore && (
          <div className="tk-more"><button type="button" onClick={loadOlder} disabled={thread.isFetching}>{t("Ältere Nachrichten laden", "Load older messages")}</button></div>
        )}
        {data && data.messages.length === 0 && data.notes.length === 0 && pending.length === 0 && (
          <div className="tk-verlauf-leer">
            <p><strong>{t("Noch keine Nachrichten.", "No messages yet.")}</strong></p>
            <p>{p.portal === "agentur" ? t(`Schreib die erste Nachricht an ${p.title}.`, `Write the first message to ${p.title}.`) : t("Schreib deiner Agentur, wenn du eine Frage hast oder etwas teilen möchtest.", "Write to your agency if you have a question or want to share something.")}</p>
          </div>
        )}
        {items.map((it) => {
          if (it.kind === "day") return <p key={it.key} className="tk-day"><span>{it.label}</span></p>;
          if (it.kind === "new") return <p key={it.key} className="tk-new">{t("Neue Nachrichten", "New messages")}</p>;
          if (it.kind === "note") {
            return (
              <NoteBubble key={it.key} dataKey={it.key} n={it.note} author={it.mine ? t("Du", "You") : p.memberName?.(it.note.author_user_id) ?? t("Team", "Team")}
                names={(mentionable.data ?? []).map((x) => x.display_name)} timeZone={p.timeZone} lang={lang} query={suchOffen ? suche : ""}
                current={aktiv === it.key} flash={blink === it.key} />
            );
          }
          const m = it.message;
          const zitat = m.reply_to ? byId.get(m.reply_to) ?? null : null;
          return (
            <Bubble
              key={it.key}
              dataKey={it.key}
              m={m}
              mine={it.mine}
              gruppe={lage.get(it.key) ?? { anfang: true, ende: true }}
              sender={p.senderName?.(m) ?? null}
              timeZone={p.timeZone}
              card={m.link_kind && m.link_id ? linkCard(m.link_kind, m.link_id, objs, p.portal, p.talentName, lang) : null}
              attachments={attachmentsOf.get(m.id) ?? []}
              chips={reactionChips(data?.reactions ?? [], m.id, me)}
              busy={reacting.busy !== null}
              onReact={(emoji) => void reacting.run("reaktion", () => toggleReaction(api, m.id, emoji))}
              canRetract={canRetract(m, me)}
              onRetract={() => void reacting.run("zurueckziehen", () => retractMessage(api, m.id), t("Nachricht zurückgezogen.", "Message withdrawn."))}
              lang={lang}
              reach={erreichbar}
              query={suchOffen ? suche : ""}
              current={aktiv === it.key}
              flash={blink === it.key}
              quote={m.reply_to ? { who: zitat ? wer(zitat) : null, text: quoteText(zitat, lang) } : null}
              onQuote={m.reply_to ? () => springeZu(m.reply_to!) : undefined}
              onReply={() => {
                setReply({ id: m.id, who: wer(m), text: quoteText(m, lang, 90) });
                window.setTimeout(() => document.getElementById(`${ids}message`)?.focus(), 0);
              }}
            />
          );
        })}
        {pending.map((x) => (
          <div key={x.key} className="tk-zeile mine tk-g-anfang tk-g-ende">
            <div className={`bubble mine tk-ecke ${x.state === "failed" ? "tk-failed" : "tk-pending"}`} role={x.state === "failed" ? "alert" : undefined}>
              {x.text.trim() && <p>{x.text.trim()}</p>}
              {x.files.length > 0 && <p>{fileOnlyBody(x.files.map((f) => f.name))}</p>}
              {x.state === "failed" ? (
                <>
                  <small className="tk-meta">{t("Nicht gesendet.", "Not sent.")} {x.error}</small>
                  <div className="row tk-nochmal">
                    <button type="button" className="primary" onClick={() => void retry(x)}>{t("Erneut senden", "Send again")}</button>
                    <button type="button" onClick={() => setPending((list) => list.filter((y) => y.key !== x.key))}>{t("Verwerfen", "Discard")}</button>
                  </div>
                </>
              ) : (
                <small className="tk-meta"><span aria-hidden="true">◷ </span>{t("Wird gesendet …", "Sending …")}</small>
              )}
            </div>
          </div>
        ))}
      </div>
      {partial && (
        <div className="tk-faden-hinweis tk-faden-hinweis-fehler" role="alert">
          <span>{t("Die Nachricht ist gesendet, aber nicht jede Datei ist angekommen:", "The message was sent, but not every file arrived:")} {partial.errors.join(" · ")}</span>
          <button type="button" className="primary" onClick={() => void retryPartial()}>{t("Datei erneut anhängen", "Attach file again")}</button>
          <button type="button" onClick={() => setPartial(null)}>{t("Schließen", "Close")}</button>
        </div>
      )}
      <Composer portal={p.portal} objs={objs} onSend={queue} lang={lang} talentName={p.talentName} vorbelegt={p.vorbelegt} ids={ids}
        reply={reply} onClearReply={() => setReply(null)}
        notes={p.portal === "agentur" ? { people: mentionable.data ?? [], me, onNote: saveNote } : undefined} />
    </section>
  );
}

/**
 * Unterzeile im Kopf (Agentur, Befunde 3, 10, 12): erreicht der Chat den Creator im Portal, nur per Mail oder gar nicht?
 * Den ganzen Satz zeigt der Hinweis beim Darüberfahren; „Einladen“ führt ins Profil.
 */
function Unterzeile({ talentId, name, reach, archiviert }: { talentId: string; name: string; reach: TalentReach; archiviert: boolean }) {
  if (archiviert) return <>{t("Archiviert · sieht neue Nachrichten erst nach der Reaktivierung", "Archived · sees new messages only after reactivation")}</>;
  if (reach.hasAccount) return <>{t("Im Portal erreichbar", "Reachable in the portal")}</>;
  const mail = !!reach.email && reach.chatMail;
  const satz = mail
    ? t(`${name} hat noch keinen Zugang zum Portal. Nachrichten erreichen ${name} nur per Mail an ${reach.email} (Sammelmail mit dem ganzen Text); Antworten kommen per Mail an die Agentur.`,
      `${name} has no portal access yet. Messages reach ${name} only by email at ${reach.email} (digest email with the full text); replies come by email to the agency.`)
    : t(`${name} hat keinen Zugang zum Portal und bekommt keine Chat-Mails${reach.email ? "" : " (keine E-Mail-Adresse)"}: Nachrichten erreichen ${name} nicht.`,
      `${name} has no portal access and receives no chat emails${reach.email ? "" : " (no email address)"}: messages do not reach ${name}.`);
  return (
    <span title={satz}>
      <span className="sr-only">{satz} </span>
      <span aria-hidden="true">{t("Kein Portal-Zugang", "No portal access")} · {mail ? t("Nachrichten gehen per Mail", "Messages go by email") : t("nicht erreichbar", "not reachable")} · </span>
      <Link to={`/agentur/creator/${talentId}`}>{t("Einladen", "Invite")}</Link>
    </span>
  );
}

/** Letzte Mail zurückgekommen oder als Spam gemeldet: schmale Warnzeile mit Weg ins Profil. */
function Ruecklaeufer({ talentId, reach, timeZone }: { talentId: string; reach: TalentReach; timeZone: string }) {
  const lp = reach.lastProblem!;
  return (
    <div className="tk-faden-hinweis tk-faden-hinweis-fehler" role="alert">
      <span>
        {lp.state === "complained" ? t("Die letzte Mail wurde als Spam gemeldet", "The last email was reported as spam")
          : lp.state === "failed" ? t("Die letzte Mail an diese Adresse ließ sich nicht zustellen", "The last email to this address could not be delivered")
          : t("Die letzte Mail an diese Adresse ist zurückgekommen", "The last email to this address bounced")}
        {` (${date(lp.at, timeZone)}${lp.detail ? `: ${lp.detail}` : ""}). `}
        <Link to={`/agentur/creator/${talentId}`}>{t("Adresse im Profil prüfen", "Check the address in the profile")}</Link>
      </span>
    </div>
  );
}

/**
 * ⋯-Menü im Kopf (Agentur): Zustellung an den Creator. Schalter für Hinweis- und Chat-Mails, wenn der Creator das per
 * WhatsApp oder Telefon wünscht; im Mini-Chat nur die Übersicht ohne Schalter (wie bisher). Escape und Klick daneben schließen.
 */
function FadenMenue({ ids, talentId, name, reach, timeZone, compact, profil }: {
  ids: string; talentId: string; name: string; reach: TalentReach; timeZone: string; compact?: boolean; profil: string | null;
}) {
  const { busy, run } = useAction();
  const [offen, setOffen] = useState(false);
  const huelle = useRef<HTMLDivElement>(null);
  const knopf = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (!offen) return;
    const weg = (e: PointerEvent) => { if (!huelle.current?.contains(e.target as Node)) setOffen(false); };
    const taste = (e: globalThis.KeyboardEvent) => {
      if (e.key !== "Escape") return;
      e.stopPropagation();
      setOffen(false);
      knopf.current?.focus();
    };
    document.addEventListener("pointerdown", weg);
    document.addEventListener("keydown", taste, true);
    return () => {
      document.removeEventListener("pointerdown", weg);
      document.removeEventListener("keydown", taste, true);
    };
  }, [offen]);
  const via: Record<string, string> = { creator: t("vom Creator", "by the creator"), team: t("vom Team", "by the team"), link: t("über den Abmeldelink", "via the unsubscribe link"), complaint: t("nach einer Spam-Meldung", "after a spam complaint") };
  const label = t("Weitere Aktionen", "More actions");
  return (
    <div className="tk-faden-menue-huelle" ref={huelle}>
      <button ref={knopf} type="button" className="tk-faden-knopf" aria-expanded={offen} aria-controls={`${ids}faden-menue`} aria-label={label} title={label}
        onClick={() => setOffen((v) => !v)}>
        <Icon name="more" />
      </button>
      {offen && (
        <div className="tk-faden-menue" id={`${ids}faden-menue`} role="group" aria-label={t(`Zustellung an ${name}`, `Delivery to ${name}`)}>
          <strong>{t(`Zustellung an ${name}`, `Delivery to ${name}`)}</strong>
          <small className="tk-reach">{t("Mails an ", "Emails to ")}{name}{t(": Hinweise ", ": notices ")}{reach.infoMail ? t("an", "on") : t("aus", "off")} {t("· Chat ", "· chat ")}{reach.chatMail ? t("an", "on") : t("aus", "off")}{reach.taskMode === "daily" ? t(" · Tagesübersicht", " · daily summary") : ""}</small>
          {!compact && (
            <div className="stack tk-faden-menue-schalter">
              <small>{t("Wünscht der Creator weniger Mails (zum Beispiel per WhatsApp oder am Telefon), stell es hier ein. Aufgaben, Rechnungen und Sicherheitsmails kommen immer.", "If the creator wants fewer emails (for example via WhatsApp or by phone), set it here. Tasks, invoices and security emails always go out.")}</small>
              <label className="row">
                <input type="checkbox" checked={reach.infoMail} disabled={busy !== null}
                  onChange={(e) => void run("info", () => saveTalentMailPrefs(api, talentId, { infoMail: e.target.checked }), t("Gespeichert.", "Saved."))} />
                {t("Hinweis-Mails (Termine, bestätigte Zahlungen, Erinnerungen)", "Notice emails (appointments, confirmed payments, reminders)")}</label>
              <label className="row">
                <input type="checkbox" checked={reach.chatMail} disabled={busy !== null}
                  onChange={(e) => void run("chat", () => saveTalentMailPrefs(api, talentId, { chatMail: e.target.checked }), t("Gespeichert.", "Saved."))} />
                {t("Chat-Mails bei ungelesenen Nachrichten", "Chat emails for unread messages")}</label>
              <label className="row">
                <input type="checkbox" checked={reach.taskMode === "daily"} disabled={busy !== null}
                  onChange={(e) => void run("modus", () => saveTalentMailPrefs(api, talentId, { taskMode: e.target.checked ? "daily" : "instant" }), t("Gespeichert.", "Saved."))} />
                {t("Einmal am Tag als Übersicht statt einzelner Mails", "Once a day as a summary instead of single emails")}</label>
              {reach.changedVia && reach.changedAt && <small>{t("Zuletzt geändert ", "Last changed ")}{via[reach.changedVia] ?? ""} {t("am ", "on ")}{date(reach.changedAt, timeZone)} {t("(im Protokoll vermerkt).", "(recorded in the log).")}</small>}
            </div>
          )}
          <div className="tk-faden-menue-links">
            {profil && <Link to={profil}>{t("Creator-Profil öffnen", "Open creator profile")}</Link>}
            {!reach.hasAccount && <Link to={`/agentur/creator/${talentId}`}>{t("Im Profil einladen", "Invite in the profile")}</Link>}
          </div>
        </div>
      )}
    </div>
  );
}

/** Abwesenheit der zuständigen Person (Praxisfall): „Kim ist bis 10.10. abwesend, es antwortet Sam.“ */
function Abwesend({ contacts, portal, lang }: { contacts: ChatContact[]; portal: Portal; lang: "de" | "en" }) {
  const away = contacts.filter((c) => c.away_until);
  if (!away.length) return null;
  const fmt = (d: string) => (lang === "en" ? dateOf(d, "en") : `${d.slice(8, 10)}.${d.slice(5, 7)}.`);
  return (
    <div className="tk-faden-hinweis" role="status">
      {away.map((c) => (
        <span key={c.user_id}>
          {lang === "en"
            ? `${c.display_name} is away until ${fmt(c.away_until!)}${c.substitute_name ? `; ${c.substitute_name} will reply` : ""}.`
            : `${c.display_name} ist bis ${fmt(c.away_until!)} abwesend${c.substitute_name ? `, es antwortet ${c.substitute_name}` : ""}.`}
          {portal === "agentur" && !c.substitute_name ? t(" Nachrichten gehen an den Admin.", " Messages go to the admin.") : ""}{" "}
        </span>
      ))}
    </div>
  );
}

/**
 * D-1 S6: Die Chatseite füllt das Fenster ohne Seiten-Scroll. Die Höhe des Portal-Kopfs (am Handy mit Agenturwahl zweizeilig)
 * und der unteren Leiste wird gemessen und als CSS-Variable gesetzt; ohne Messung gelten --portal-header und 69 px.
 */
export function useChatSeitenHoehe() {
  useLayoutEffect(() => {
    const wurzel = document.documentElement;
    const kopf = document.querySelector<HTMLElement>(".portal-header");
    const unten = document.querySelector<HTMLElement>(".bottomnav");
    const messen = () => {
      if (kopf) wurzel.style.setProperty("--tk-kopf-hoehe", `${kopf.offsetHeight}px`);
      if (unten) wurzel.style.setProperty("--tk-unten-hoehe", `${unten.offsetHeight}px`);
    };
    messen();
    const ro = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(messen);
    if (kopf) ro?.observe(kopf);
    if (unten) ro?.observe(unten);
    return () => {
      ro?.disconnect();
      wurzel.style.removeProperty("--tk-kopf-hoehe");
      wurzel.style.removeProperty("--tk-unten-hoehe");
    };
  }, []);
}

/**
 * Eine Zeile der Gesprächsliste (Chatseite und Mini-Chat): Kürzel 36 px, Name fett, letzte Nachricht einzeilig grau,
 * Zeit rechts oben, Ungelesen-Zahl als grüner Kreis rechts unten. Als Link (Chatseite) oder Knopf (Mini-Chat).
 */
export function FadenZeile({ name, preview, when, unread, current, to, onClick, className }: {
  name: string; preview: string; when: string | null; unread: number; current?: boolean; to?: string; onClick?: () => void; className?: string;
}) {
  const inhalt = (
    <>
      <span className="tk-chat-avatar" aria-hidden="true">{initials(name)}</span>
      <span className="tk-faden-zeile-text">
        <span className="tk-faden-zeile-oben"><strong>{name}</strong>{when && <time>{when}</time>}</span>
        <span className="tk-faden-zeile-unten">
          <span className="tk-faden-zeile-vorschau">{preview}</span>
          {unread > 0 && <span className="tk-ungelesen" aria-label={t(`${unread} ungelesen`, `${unread} unread`)}>{unread > 99 ? "99+" : unread}</span>}
        </span>
      </span>
    </>
  );
  const cls = ["tk-faden-zeile", unread > 0 ? "tk-hat-neue" : null, className].filter(Boolean).join(" ");
  return to
    ? <Link className={cls} to={to} aria-current={current ? "page" : undefined}>{inhalt}</Link>
    : <button type="button" className={cls} onClick={onClick}>{inhalt}</button>;
}

// ── Blase ───────────────────────────────────────────────────────────────────

/** Text mit Treffern der Suche (<mark>) */
function Hervorgehoben({ text, query }: { text: string; query: string }) {
  if (!query.trim()) return <>{text}</>;
  return <>{highlightParts(text, query).map((x, i) => (x.hit ? <mark key={i} className="tk-treffer">{x.text}</mark> : <Fragment key={i}>{x.text}</Fragment>))}</>;
}

function Bubble({ m, mine, gruppe, sender, timeZone, card, attachments, chips, busy, onReact, canRetract: retractable, onRetract, lang, reach, query, current, flash, quote, onQuote, onReply, dataKey }: {
  m: MessageRow;
  mine: boolean;
  /** D-1 S6: Lage in der Gruppe (Folgenachrichten desselben Absenders): Name nur oben, Ecke nur unten */
  gruppe: GruppenLage;
  sender: string | null;
  timeZone: string;
  card: ReturnType<typeof linkCard> | null;
  attachments: AttachmentRow[];
  chips: ReactionChip[];
  busy: boolean;
  onReact: (emoji: string) => void;
  canRetract: boolean;
  onRetract: () => void;
  lang: "de" | "en";
  reach?: "portal" | "mail" | "none";
  /** K-525: Suche (Treffer hervorheben), aktueller Treffer, kurz markiert nach einem Sprung */
  query: string;
  current: boolean;
  flash: boolean;
  /** K-525: Zitat der Nachricht, auf die geantwortet wurde (Klick springt dorthin) */
  quote: { who: string | null; text: string } | null;
  onQuote?: () => void;
  onReply: () => void;
  dataKey: string;
}) {
  const t = (de: string, en: string) => (lang === "en" ? en : de);
  const tm = (iso: string) => time(iso, timeZone, lang === "en" ? "en-GB" : "de-DE");
  const [frage, setFrage] = useState(false);
  const [wahl, setWahl] = useState(false);
  // Aktionen am Handy: Langdruck auf die Blase oder ⋯ öffnet die Leiste; Tippen daneben schließt sie
  const [offen, setOffen] = useState(false);
  const zeile = useRef<HTMLDivElement>(null);
  const druck = useRef<number | null>(null);
  useEffect(() => {
    if (!offen && !wahl) return;
    const weg = (e: PointerEvent) => {
      if (zeile.current?.contains(e.target as Node)) return;
      setOffen(false);
      setWahl(false);
    };
    document.addEventListener("pointerdown", weg);
    return () => document.removeEventListener("pointerdown", weg);
  }, [offen, wahl]);
  const loslassen = () => {
    if (druck.current !== null) window.clearTimeout(druck.current);
    druck.current = null;
  };
  const zustand = `${current ? " tk-aktuell" : ""}${flash ? " tk-blink" : ""}`;
  const lageCls = `${gruppe.anfang ? " tk-g-anfang" : ""}${gruppe.ende ? " tk-g-ende" : ""}`;
  const ecke = gruppe.ende ? " tk-ecke" : "";
  // Zurückgezogen (Befund 17): Text, Verknüpfung und Dateien sind weg, der Zeitpunkt bleibt sichtbar
  if (m.retracted_at) {
    return (
      <div className={`tk-zeile${mine ? " mine" : ""}${lageCls}`} data-key={dataKey}>
        <div className={`bubble${mine ? " mine" : ""} tk-retracted${ecke}${zustand}`}>
          {gruppe.anfang && sender && <small className="tk-absender">{sender}</small>}
          <p><em>{mine ? t("Du hast diese Nachricht zurückgezogen.", "You withdrew this message.") : t("Nachricht zurückgezogen.", "Message withdrawn.")}</em></p>
          <small className="tk-meta">{tm(m.created_at)} · {t("zurückgezogen", "withdrawn")} {tm(m.retracted_at)}</small>
        </div>
      </div>
    );
  }
  // Nachrichten, die nur Dateien tragen, zeigen statt des Platzhaltertexts die Dateien (wie im Altportal)
  const hideBody = attachments.length > 0 && m.body === fileOnlyBody(attachments.map((a) => a.file_name));
  // Rundnachricht (0056): die Agentur sieht, dass sie an mehrere ging; der Creator, dass Antworten nur die Agentur sieht
  const rund = m.broadcast_id ? (mine ? t("Rundnachricht", "Group message") : t("Rundnachricht deiner Agentur · deine Antwort sieht nur die Agentur", "Group message from your agency · only your agency sees your reply")) : null;
  const kopf = [gruppe.anfang ? sender : null, rund].filter(Boolean).join(" · ");
  const status = mine ? zustellung(m, reach) : null;
  const statusText = mine ? deliveryLabel(m, { lang, reach }) : "";
  const meta = (
    <>
      <time dateTime={m.created_at}>{tm(m.created_at)}</time>
      {status && <span className={`tk-zustellung tk-zustellung-${status}`} title={statusText}><span aria-hidden="true">{ZUSTELL_ZEICHEN[status]}</span><span className="sr-only">{statusText}</span></span>}
    </>
  );
  const text = m.body && !hideBody;
  // Endet die Blase mit Text, steht die Zeit unten rechts in der letzten Zeile (Platzhalter in gleicher Breite)
  const zeitImText = !!text && !card && attachments.length === 0;
  const aktionen = t("Aktionen zur Nachricht", "Message actions");
  return (
    <div ref={zeile} className={`tk-zeile${mine ? " mine" : ""}${lageCls}${offen || wahl ? " tk-aktionen-offen" : ""}`} data-key={dataKey}
      onPointerDown={(e) => {
        if (e.pointerType !== "touch" || (e.target as HTMLElement).closest("button, a")) return;
        loslassen();
        druck.current = window.setTimeout(() => setOffen(true), 450);
      }}
      onPointerUp={loslassen} onPointerCancel={loslassen} onPointerLeave={loslassen}>
      <div className={`bubble${mine ? " mine" : ""}${ecke}${zustand}`}>
        {kopf && <small className="tk-absender">{kopf}</small>}
        {quote && (
          <button type="button" className="tk-zitat" onClick={onQuote} title={t("Zur Nachricht springen", "Jump to the message")}>
            {quote.who && <strong>{quote.who}</strong>}
            <span>{quote.text}</span>
          </button>
        )}
        {/* Praxistest K-123: Links in Nachrichten sind anklickbar (nur http und https; alles andere bleibt Text) */}
        {text && <p>{textParts(m.body).map((teil, i) => (teil.href
          ? <a key={i} href={teil.href} target="_blank" rel="noopener noreferrer nofollow"><Hervorgehoben text={teil.text} query={query} /></a>
          : <Fragment key={i}><Hervorgehoben text={teil.text} query={query} /></Fragment>))}{zeitImText && <span className="tk-zeit-platz" aria-hidden="true">{meta}</span>}</p>}
        {card && <ObjektKarte card={card} />}
        {attachments.map((a) => <AttachmentView key={a.id} a={a} lang={lang} query={query} />)}
        <small className={`tk-meta${zeitImText ? " tk-meta-schwebend" : ""}`}>{meta}</small>
        <div className="tk-aktionsleiste" role="toolbar" aria-label={aktionen}>
          <button type="button" className="tk-antworten" onClick={() => { setOffen(false); onReply(); }} aria-label={t("Mit Zitat antworten", "Reply with quote")} title={t("Antworten", "Reply")}>
            <Icon name="reply" />
          </button>
          <button type="button" className="tk-reagieren" aria-expanded={wahl} aria-label={wahl ? t("Reaktionen schließen", "Close reactions") : t("Reagieren", "React")} title={t("Reagieren", "React")}
            onClick={() => setWahl((v) => !v)}>
            <Icon name="smile" />
          </button>
          {retractable && (
            <button type="button" className="tk-zurueckziehen" disabled={busy} onClick={() => { setOffen(false); setFrage(true); }} aria-label={t("Zurückziehen", "Withdraw")}
              title={t(`Zurückziehen (innerhalb von ${RETRACT_HOURS} Stunden nach dem Senden möglich)`, `Withdraw (possible within ${RETRACT_HOURS} hours after sending)`)}>
              <Icon name="withdraw" />
            </button>
          )}
        </div>
        <button type="button" className="tk-aktionen-mehr" aria-expanded={offen} aria-label={aktionen} onClick={() => setOffen((v) => !v)}><Icon name="more" /></button>
        {wahl && <EmojiWahl chips={chips} busy={busy} lang={lang} onReact={(e) => { setWahl(false); setOffen(false); onReact(e); }} onClose={() => setWahl(false)} />}
      </div>
      {chips.length > 0 && <Reactions chips={chips} busy={busy} onReact={onReact} lang={lang} />}
      {frage && (
        <div className="row tk-retract" role="group" aria-label={t("Zurückziehen bestätigen", "Confirm withdrawal")}>
          <small>{t("Zurückziehen? Text, Verknüpfung und Dateien verschwinden für beide Seiten.", "Withdraw? Text, link and files disappear for both sides.")}</small>
          <button type="button" className="danger" disabled={busy} onClick={() => { setFrage(false); onRetract(); }}>{t("Zurückziehen", "Withdraw")}</button>
          <button type="button" disabled={busy} onClick={() => setFrage(false)}>{t("Behalten", "Keep")}</button>
        </div>
      )}
    </div>
  );
}

/** K-525: interne Notiz des Teams im Verlauf als gelbe Karte in der Mitte; der Creator sieht sie nie (eigene Tabelle, 0393). */
function NoteBubble({ n, author, names, timeZone, lang, query, current, flash, dataKey }: {
  n: NoteRow; author: string; names: string[]; timeZone: string; lang: "de" | "en"; query: string; current: boolean; flash: boolean; dataKey: string;
}) {
  const t = (de: string, en: string) => (lang === "en" ? en : de);
  return (
    <div className="tk-zeile tk-zeile-notiz" data-key={dataKey}>
      <div className={`bubble tk-notiz${current ? " tk-aktuell" : ""}${flash ? " tk-blink" : ""}`} title={t("Interne Notiz · nur dein Team sieht sie", "Internal note · only your team sees it")}>
        <small className="tk-notiz-kopf">
          <Icon name="lock" /><strong>{t("Nur Team", "Team only")}</strong>
          <span>· {author} · {time(n.created_at, timeZone, lang === "en" ? "en-GB" : "de-DE")}</span>
          <span className="sr-only">{t("Interne Notiz, der Creator sieht sie nicht.", "Internal note, the creator does not see it.")}</span>
        </small>
        <p>{mentionParts(n.body, names).map((x, i) => (x.mention
          ? <span key={i} className="tk-erwaehnung"><Hervorgehoben text={x.text} query={query} /></span>
          : <Fragment key={i}><Hervorgehoben text={x.text} query={query} /></Fragment>))}</p>
      </div>
    </div>
  );
}

/** Reaktionen als kleine Chips unter der Blase; Klick nimmt die eigene zurück oder reagiert ebenfalls. */
function Reactions({ chips, busy, onReact, lang }: { chips: ReactionChip[]; busy: boolean; onReact: (emoji: string) => void; lang: "de" | "en" }) {
  const en = lang === "en";
  return (
    <div className="tk-reactions">
      {chips.map((c) => (
        <button key={c.emoji} type="button" aria-pressed={c.mine} disabled={busy} onClick={() => onReact(c.emoji)}
          aria-label={`${c.emoji} ${c.count}: ${c.mine ? (en ? "remove your reaction" : "deine Reaktion zurücknehmen") : (en ? "react as well" : "ebenfalls reagieren")}`}>
          {c.emoji}{c.count > 1 ? ` ${c.count}` : ""}
        </button>
      ))}
    </div>
  );
}

/** Auswahl der sechs Reaktionen über der Mini-Leiste; Escape schließt, der Fokus springt auf die erste Reaktion. */
function EmojiWahl({ chips, busy, lang, onReact, onClose }: { chips: ReactionChip[]; busy: boolean; lang: "de" | "en"; onReact: (emoji: string) => void; onClose: () => void }) {
  const en = lang === "en";
  const mine = new Set(chips.filter((c) => c.mine).map((c) => c.emoji));
  return (
    <div className="tk-emoji-wahl" role="group" aria-label={en ? "Choose a reaction" : "Reaktion wählen"}
      onKeyDown={(e) => { if (e.key === "Escape") { e.stopPropagation(); onClose(); } }}>
      {EMOJIS.map((e, i) => (
        <button key={e} type="button" autoFocus={i === 0} aria-pressed={mine.has(e)} disabled={busy}
          aria-label={mine.has(e) ? (en ? `Remove ${e}` : `${e} zurücknehmen`) : (en ? `React with ${e}` : `Mit ${e} reagieren`)}
          onClick={() => onReact(e)}>
          {e}
        </button>
      ))}
    </div>
  );
}

/**
 * Datei aus dem privaten Bucket: PDF und Bilder in einem neuen Tab (Fenster im Klick öffnen, sonst sperrt der Browser),
 * Office-Dateien und HEIC als Download mit ihrem Namen (K-525: keine Vorschau).
 */
async function openAttachment(a: AttachmentRow) {
  const preview = isPreviewable(a.mime_type);
  const w = preview && typeof window !== "undefined" ? window.open("", "_blank") : null;
  try {
    const blob = await fetchAttachment(api, a.storage_path);
    const typed = blob.type === a.mime_type || !a.mime_type ? blob : new Blob([blob], { type: a.mime_type });
    const url = URL.createObjectURL(typed);
    if (w) w.location.href = url;
    else {
      const link = document.createElement("a");
      link.href = url;
      link.download = a.file_name;
      document.body.appendChild(link);
      link.click();
      link.remove();
    }
    setTimeout(() => URL.revokeObjectURL(url), 120_000);
  } catch (e) {
    w?.close();
    const d = describeError(e);
    toast(`${d.title} ${d.text}`.trim(), "alert");
  }
}

function AttachmentView({ a, lang, query }: { a: AttachmentRow; lang: "de" | "en"; query: string }) {
  const [url, setUrl] = useState<string | null>(null);
  const image = isImage(a.mime_type);
  useEffect(() => {
    if (!image) return;
    let alive = true;
    let current: string | null = null;
    fetchAttachment(api, a.storage_path)
      .then((b) => {
        if (!alive || !b.type.startsWith("image/")) return;
        current = URL.createObjectURL(b);
        setUrl(current);
      })
      .catch(() => undefined);
    return () => {
      alive = false;
      if (current) URL.revokeObjectURL(current);
    };
  }, [a.storage_path, image]);
  if (url) {
    return (
      <button type="button" className="tk-image" onClick={() => void openAttachment(a)} aria-label={lang === "en" ? `Open ${a.file_name}` : `${a.file_name} öffnen`}>
        <img src={url} alt={a.file_name} />
      </button>
    );
  }
  const download = !isPreviewable(a.mime_type);
  // D-1 S6: Datei als kompakte Zeile (Zeichen, Name, Größe und Art)
  return (
    <button type="button" className="tk-file tk-datei" onClick={() => void openAttachment(a)}
      aria-label={download ? (lang === "en" ? `Download ${a.file_name}` : `${a.file_name} herunterladen`) : (lang === "en" ? `Open ${a.file_name}` : `${a.file_name} öffnen`)}>
      <span className="tk-datei-zeichen" aria-hidden="true"><Icon name="file" /></span>
      <span className="tk-datei-text">
        <strong><Hervorgehoben text={a.file_name} query={query} /></strong>
        <small>{fileSizeLabel(a.size_bytes)} · {fileKindLabel(a.mime_type)}{download ? (lang === "en" ? " · download" : " · herunterladen") : ""}</small>
      </span>
    </button>
  );
}

// ── Eingabe ─────────────────────────────────────────────────────────────────

const KARTEN_ICON: Record<LinkKind, IconName> = { deal: "deal", invoice: "receipt", contract: "file" };

/** K-515: Objekt-Karte in der Nachricht: Zeichen der Art, Titel, Untertitel (Nummer, Betrag, Fälligkeit) und aktueller Status */
function ObjektKarte({ card }: { card: LinkCard }) {
  const inhalt = (
    <>
      <span className="tk-objekt-zeichen" aria-hidden="true"><Icon name={card.kind ? KARTEN_ICON[card.kind] : "link"} /></span>
      <span className="tk-objekt-text"><strong>{card.title}</strong><small>{card.sub}</small></span>
      {card.pill && <PillView pill={card.pill} />}
    </>
  );
  return card.to ? <Link className="object tk-objekt" to={card.to}>{inhalt}</Link> : <span className="object tk-objekt">{inhalt}</span>;
}

function Composer({ portal, objs, onSend, lang, talentName, vorbelegt, ids, reply, onClearReply, notes }: {
  portal: Portal; objs: LinkObjects; onSend: (text: string, files: File[], link: Pending["link"]) => void; lang: "de" | "en"; talentName: string;
  /** K-517: aus „Im Chat teilen“ vorbelegtes Objekt (kind:id) */
  vorbelegt?: string | null;
  ids: string;
  /** K-525: Antwort mit Zitat */
  reply: Reply | null;
  onClearReply: () => void;
  /** K-525: interne Notiz mit @-Erwähnung (nur Agentur) */
  notes?: { people: Mentionable[]; me: string | null; onNote: (text: string, mentions: string[]) => Promise<void> };
}) {
  const t = (de: string, en: string) => (lang === "en" ? en : de);
  const [text, setText] = useState("");
  const [files, setFiles] = useState<File[]>([]);
  const [linkValue, setLinkValue] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [dragging, setDragging] = useState(false);
  // K-529 d: Bilder werden vor dem Anhängen verkleinert; so lange steht ein Hinweis da
  const [verkleinere, setVerkleinere] = useState(0);
  // K-514: ein sichtbarer Knopf „Anhängen“ mit Datei oder Objekt; die Objekt-Auswahl als Fenster mit Reitern und Suche
  const [menue, setMenue] = useState(false);
  const [wahl, setWahl] = useState(false);
  const [reiter, setReiter] = useState<LinkKind>("deal");
  const [suche, setSuche] = useState("");
  // K-525: Nachricht an den Creator oder interne Notiz fürs Team
  const [modus, setModus] = useState<"nachricht" | "notiz">("nachricht");
  const [speichert, setSpeichert] = useState(false);
  const [caret, setCaret] = useState(0);
  const [vorschlagNr, setVorschlagNr] = useState(0);
  const [vorschlagZu, setVorschlagZu] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const feld = useRef<HTMLTextAreaElement>(null);
  const anhang = useRef<HTMLDivElement>(null);
  const caretSoll = useRef<number | null>(null);
  const tabs = useMemo(() => linkTabs(objs, portal, talentName, lang), [objs, portal, talentName, lang]);
  const link = parseLinkValue(linkValue);
  const gewaehlt = link ? linkCard(link.kind, link.id, objs, portal, talentName, lang) : null;
  const notiz = !!notes && modus === "notiz";
  // Vorbelegung nur, wenn der Creator das Objekt auch öffnen kann (sonst bliebe die Karte für ihn leer)
  const erlaubt = tabs.some((x) => x.items.some((i) => i.value === vorbelegt));
  useEffect(() => {
    if (vorbelegt && erlaubt) setLinkValue(vorbelegt);
  }, [vorbelegt, erlaubt]);
  // Antworten schaltet auf „Nachricht“ (Notizen lassen sich nicht an den Creator zitieren)
  useEffect(() => {
    if (reply) setModus("nachricht");
  }, [reply]);

  const mq = notiz && !vorschlagZu ? mentionQuery(text, caret) : null;
  const vorschlaege = mq && notes ? mentionSuggestions(notes.people, mq.query, notes.me) : [];

  async function addFiles(list: FileList | readonly File[]) {
    const raw = [...list];
    if (!raw.length) return;
    setVerkleinere((n) => n + 1);
    let incoming: File[];
    try {
      incoming = await Promise.all(raw.map((f) => verkleinereBild(f).catch(() => f)));
    } finally {
      setVerkleinere((n) => n - 1);
    }
    const problems = incoming.map((f) => checkFile(f)).filter((x): x is string => !!x);
    const ok = incoming.filter((f) => !checkFile(f));
    const room = MAX_FILES - files.length;
    if (ok.length > room) problems.push(t(`Höchstens ${MAX_FILES} Dateien je Nachricht.`, `At most ${MAX_FILES} files per message.`));
    setFiles((cur) => [...cur, ...ok.slice(0, Math.max(0, MAX_FILES - cur.length))]);
    setError(problems.length ? `${t("Die Datei wurde nicht hochgeladen.", "The file was not uploaded.")} ${problems.join(" ")}` : null);
  }

  async function submit(e?: FormEvent) {
    e?.preventDefault();
    const clean = text.trim();
    if (notiz && notes) {
      if (!clean) {
        setError(t("Schreib eine Notiz für dein Team.", "Write a note for your team."));
        return;
      }
      if (clean.length > MAX_BODY) {
        setError(t(`Die Notiz ist zu lang (höchstens ${MAX_BODY.toLocaleString("de-DE")} Zeichen).`, `The note is too long (at most ${MAX_BODY.toLocaleString("en-GB")} characters).`));
        return;
      }
      setSpeichert(true);
      try {
        await notes.onNote(clean, parseMentions(clean, notes.people));
        setText("");
        setError(null);
      } catch (err) {
        const d = describeError(err);
        setError(`${d.title} ${d.text}`.trim());
      } finally {
        setSpeichert(false);
      }
      return;
    }
    if (verkleinere > 0) {
      setError(t("Einen Moment, das Bild wird noch verkleinert.", "One moment, the image is still being resized."));
      return;
    }
    if (!clean && !files.length && !link) {
      setError(t("Schreib eine Nachricht, hänge eine Datei an oder verknüpfe ein Objekt.", "Write a message, attach a file or link an item."));
      return;
    }
    if (clean.length > MAX_BODY) {
      setError(t(`Die Nachricht ist zu lang (höchstens ${MAX_BODY.toLocaleString("de-DE")} Zeichen).`, `The message is too long (at most ${MAX_BODY.toLocaleString("en-GB")} characters).`));
      return;
    }
    onSend(text, files, link);
    setText("");
    setFiles([]);
    setLinkValue("");
    setError(null);
  }

  function waehle(person: Mentionable) {
    if (!mq) return;
    const r = insertMention(text, mq.start, caret, person.display_name);
    caretSoll.current = r.caret;
    setText(r.text);
    setCaret(r.caret);
  }
  // Cursor hinter die eingesetzte Erwähnung, gleich nach dem Zeichnen (vor dem nächsten Tastendruck)
  useLayoutEffect(() => {
    const c = caretSoll.current;
    if (c === null || !feld.current) return;
    caretSoll.current = null;
    feld.current.focus();
    feld.current.setSelectionRange(c, c);
  }, [text]);

  // D-1 S6: Feld beginnt einzeilig und wächst bis fünf Zeilen, danach scrollt es in sich
  useLayoutEffect(() => {
    const el = feld.current;
    if (!el) return;
    el.style.height = "auto";
    const cs = getComputedStyle(el);
    const zeile = parseFloat(cs.lineHeight) || 20;
    const rand = parseFloat(cs.borderTopWidth) + parseFloat(cs.borderBottomWidth);
    const max = zeile * 5 + parseFloat(cs.paddingTop) + parseFloat(cs.paddingBottom);
    el.style.height = `${Math.min(el.scrollHeight, max) + rand}px`;
    el.style.overflowY = el.scrollHeight > max + 1 ? "auto" : "hidden";
  }, [text, notiz]);
  // „+“-Menü schließt bei Klick daneben
  useEffect(() => {
    if (!menue) return;
    const weg = (e: PointerEvent) => { if (!anhang.current?.contains(e.target as Node)) setMenue(false); };
    document.addEventListener("pointerdown", weg);
    return () => document.removeEventListener("pointerdown", weg);
  }, [menue]);

  function onKeyDown(e: KeyboardEvent<HTMLTextAreaElement>) {
    // Während einer Eingabe mit Kompositionsfenster (Japanisch, Chinesisch, Akzente) sendet Enter nie (Safari meldet keyCode 229)
    const komponiert = e.nativeEvent.isComposing || e.keyCode === 229;
    if (vorschlaege.length) {
      if (e.key === "ArrowDown" || e.key === "ArrowUp") {
        e.preventDefault();
        setVorschlagNr((n) => (n + (e.key === "ArrowDown" ? 1 : -1) + vorschlaege.length) % vorschlaege.length);
        return;
      }
      if ((e.key === "Enter" || e.key === "Tab") && !e.shiftKey && !komponiert) {
        e.preventDefault();
        waehle(vorschlaege[Math.min(vorschlagNr, vorschlaege.length - 1)]!);
        return;
      }
      if (e.key === "Escape") {
        e.preventDefault();
        setVorschlagZu(true);
        return;
      }
    }
    if (e.key === "Escape" && reply) {
      e.preventDefault();
      onClearReply();
      return;
    }
    if (e.key === "Enter" && !e.shiftKey && !komponiert) {
      e.preventDefault();
      void submit();
    }
  }
  function onPaste(e: ClipboardEvent<HTMLTextAreaElement>) {
    if (e.clipboardData.files.length && !notiz) {
      e.preventDefault();
      void addFiles(e.clipboardData.files);
    }
  }
  const hasFiles = (e: DragEvent) => [...e.dataTransfer.types].includes("Files");
  const tab = tabs.find((x) => x.kind === reiter) ?? tabs[0]!;
  const q = suche.trim().toLowerCase();
  const treffer = q ? tab.items.filter((i) => i.search.includes(q)) : tab.items;
  const gesperrt = q ? tab.blocked.filter((b) => b.title.toLowerCase().includes(q)) : tab.blocked;

  return (
    <>
    <form id={`${ids}chat-form`} data-schreiben className={`tk-chat-form${dragging ? " tk-drop" : ""}${notiz ? " tk-notiz-modus" : ""}`} onSubmit={(e) => void submit(e)}
      onDragOver={(e) => {
        if (!hasFiles(e) || notiz) return;
        e.preventDefault();
        setDragging(true);
      }}
      onDragLeave={() => setDragging(false)}
      onDrop={(e) => {
        if (!hasFiles(e) || notiz) return;
        e.preventDefault();
        setDragging(false);
        void addFiles(e.dataTransfer.files);
      }}>
      {/* D-1 S6: über dem Feld kleine Reiter „Nachricht | Interne Notiz“ (nur Agentur), im Notizmodus ein kurzer Hinweis */}
      {notes && (
        <div className="tk-modus-zeile">
          <div className="tk-modus" role="radiogroup" aria-label={t("Art des Eintrags", "Entry type")}>
            <label className={!notiz ? "aktiv" : undefined}>
              <input type="radio" name={`${ids}chat-modus`} checked={!notiz} onChange={() => setModus("nachricht")} />
              <span>{t("Nachricht", "Message")}</span>
            </label>
            <label className={notiz ? "aktiv" : undefined}>
              <input type="radio" name={`${ids}chat-modus`} checked={notiz} onChange={() => { setModus("notiz"); onClearReply(); }} />
              <span>{t("Interne Notiz", "Internal note")}</span>
            </label>
          </div>
          {notiz && (
            <small className="tk-notiz-hinweis" role="note" title={t(`Nur dein Team sieht die Notiz, ${talentName} nie (auch nicht per Mail oder WhatsApp). Mit @Name erwähnst du Kolleginnen und Kollegen; sie bekommen einen Hinweis in der Glocke.`,
              `Only your team sees the note, ${talentName} never does (not by email or WhatsApp either). Type @Name to mention colleagues; they get a notice in the bell.`)}>
              <Icon name="lock" />{t("Nur dein Team sieht das", "Only your team sees this")}
            </small>
          )}
        </div>
      )}
      {reply && !notiz && (
        <div className="tk-antwort-auf" role="status">
          <Icon name="reply" />
          <span className="tk-antwort-text"><strong>{t(`Antwort an ${reply.who}`, `Reply to ${reply.who}`)}</strong><span>{reply.text}</span></span>
          <button type="button" className="tk-faden-knopf" onClick={onClearReply} aria-label={t("Zitat entfernen", "Remove quote")}>×</button>
        </div>
      )}
      {gewaehlt && !notiz && (
        <div className="tk-anhang-gewaehlt" role="status">
          <ObjektKarte card={{ ...gewaehlt, to: null }} />
          <button type="button" className="tk-faden-knopf" onClick={() => setLinkValue("")} aria-label={t("Verknüpfung entfernen", "Remove link")} title={t("Entfernen", "Remove")}>×</button>
        </div>
      )}
      {files.length > 0 && !notiz && (
        <ul className="tk-files" aria-label={t("Ausgewählte Dateien", "Selected files")}>
          {files.map((f, i) => (
            <li key={`${f.name}-${i}`}>
              <Icon name="file" />
              <span>{f.name} · {fileSizeLabel(f.size)}</span>
              <button type="button" className="tk-faden-knopf" aria-label={`${f.name} ${t("entfernen", "remove")}`} title={t("Entfernen", "Remove")} onClick={() => setFiles((cur) => cur.filter((_, j) => j !== i))}>×</button>
            </li>
          ))}
        </ul>
      )}
      {verkleinere > 0 && <small role="status" className="tk-eingabe-stand">{t("Bild wird verkleinert …", "Resizing image …")}</small>}
      {/* Eingabeleiste: „+“ (Datei, Kooperation, Rechnung, Vertrag), Textfeld (wächst bis 5 Zeilen), runder Senden-Knopf */}
      <div className="tk-eingabeleiste">
        {!notiz && (
        <div className="tk-anhang" ref={anhang}>
          <button type="button" className="tk-rund tk-plus" aria-haspopup="menu" aria-expanded={menue} aria-label={t("Anhängen", "Attach")} title={t("Anhängen", "Attach")} onClick={() => setMenue((v) => !v)}>
            <Icon name="plus" />
          </button>
          {menue && (
            <div className="tk-anhang-menue" role="menu" onKeyDown={(e) => {
              if (e.key === "Escape") {
                e.stopPropagation();
                setMenue(false);
                anhang.current?.querySelector<HTMLButtonElement>(".tk-plus")?.focus();
              }
              if (e.key === "ArrowDown" || e.key === "ArrowUp") {
                e.preventDefault();
                const alle = [...e.currentTarget.querySelectorAll<HTMLButtonElement>("[role=menuitem]")];
                const i = alle.indexOf(document.activeElement as HTMLButtonElement);
                alle[(i + (e.key === "ArrowDown" ? 1 : -1) + alle.length) % alle.length]?.focus();
              }
            }}>
              <button type="button" role="menuitem" autoFocus onClick={() => { setMenue(false); fileRef.current?.click(); }}>
                <Icon name="clip" /><span>{t("Datei oder Bild", "File or image")}<small>{fileHint(lang)}</small></span>
              </button>
              {tabs.map((x) => (
                <button key={x.kind} type="button" role="menuitem" onClick={() => { setMenue(false); setSuche(""); setReiter(x.kind); setWahl(true); }}>
                  <Icon name={KARTEN_ICON[x.kind]} /><span>{x.label}</span>
                </button>
              ))}
            </div>
          )}
          <input ref={fileRef} type="file" multiple hidden accept={FILE_ACCEPT} aria-label={t("Datei anhängen", "Attach file")}
            onChange={(e) => {
              if (e.target.files?.length) void addFiles(e.target.files);
              e.target.value = "";
            }} />
        </div>
        )}
        <label className="tk-eingabe-feld">
          <span className="sr-only">{notiz ? t("Interne Notiz", "Internal note") : t("Nachricht", "Message")}</span>
          <textarea ref={feld} id={`${ids}message`} rows={1} required={notiz || (!files.length && !link)} maxLength={MAX_BODY} value={text}
            placeholder={notiz ? t("Notiz fürs Team … @Name erwähnt", "Note for your team … @Name mentions") : t("Nachricht schreiben …", "Write a message …")}
            role={notiz ? "combobox" : undefined} aria-expanded={notiz ? vorschlaege.length > 0 : undefined} aria-controls={notiz ? `${ids}erwaehnung-liste` : undefined}
            aria-autocomplete={notiz ? "list" : undefined}
            onChange={(e) => {
              setText(e.target.value);
              setCaret(e.target.selectionStart ?? e.target.value.length);
              setVorschlagNr(0);
              setVorschlagZu(false);
            }}
            onSelect={(e) => setCaret(e.currentTarget.selectionStart ?? 0)}
            onKeyDown={onKeyDown} onPaste={onPaste} aria-describedby={`${ids}chat-hinweis`} />
          {vorschlaege.length > 0 && (
            <ul className="tk-erwaehnung-liste" id={`${ids}erwaehnung-liste`} role="listbox" aria-label={t("Team-Mitglieder", "Team members")}>
              {vorschlaege.map((v, i) => (
                <li key={v.user_id} role="option" aria-selected={i === Math.min(vorschlagNr, vorschlaege.length - 1)}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    waehle(v);
                  }}>
                  <span className="tk-chat-avatar" aria-hidden="true">{initials(v.display_name)}</span>{v.display_name}
                </li>
              ))}
            </ul>
          )}
        </label>
        <button className="primary tk-rund tk-senden" type="submit" disabled={speichert}
          aria-label={notiz ? t("Notiz speichern", "Save note") : t("Senden", "Send")} title={notiz ? t("Notiz speichern", "Save note") : t("Senden", "Send")}>
          <Icon name={notiz ? "note" : "send"} />
        </button>
      </div>
      <small id={`${ids}chat-hinweis`} className="sr-only">
        {notiz
          ? t("Enter speichert, Umschalt + Enter beginnt eine neue Zeile. @ öffnet die Liste deines Teams.", "Enter saves, Shift + Enter starts a new line. @ opens the list of your team.")
          : t(`Enter sendet, Umschalt + Enter beginnt eine neue Zeile. Dateien: ${fileHint("de")}, auch per Ziehen oder Einfügen. Bilder werden vor dem Hochladen verkleinert.`,
            `Enter sends, Shift + Enter starts a new line. Files: ${fileHint("en")}, also by dragging or pasting. Images are resized before uploading.`)}
      </small>
      {error && <p className="tk-error" role="alert">{error}</p>}
    </form>
    <Dialog open={wahl} onClose={() => setWahl(false)} title={t("Anhängen", "Attach")} wide>
      <div className="stack tk-anhang-wahl">
        <div className="tk-umschalter" role="radiogroup" aria-label={t("Art", "Type")}>
          {tabs.map((x) => (
            <label key={x.kind} className={x.kind === tab.kind ? "aktiv" : undefined}>
              <input type="radio" name="anhang-art" checked={x.kind === tab.kind} onChange={() => setReiter(x.kind)} />
              <span>{x.label} <small>{x.items.length}</small></span>
            </label>
          ))}
        </div>
        <label>
          {t("Suchen", "Search")}
          <input type="search" value={suche} onChange={(e) => setSuche(e.target.value)} placeholder={t("Nummer, Marke, Titel …", "Number, brand, title …")} />
        </label>
        {treffer.length > 0 ? (
          <ul className="tk-anhang-liste">
            {treffer.map((i) => (
              <li key={i.value}>
                <button type="button" onClick={() => { setLinkValue(i.value); setWahl(false); }} aria-pressed={i.value === linkValue}>
                  <ObjektKarte card={{ title: i.title, sub: i.sub, to: null, kind: i.kind, pill: i.pill }} />
                </button>
              </li>
            ))}
          </ul>
        ) : (
          <p className="muted">{q ? t("Nichts gefunden.", "Nothing found.") : portal === "agentur" ? t(`Mit ${talentName} gibt es hier noch nichts, das sich anhängen lässt.`, `There is nothing here yet with ${talentName} that can be attached.`) : t("Hier gibt es noch nichts, das sich anhängen lässt.", "There is nothing here yet that can be attached.")}</p>
        )}
        {gesperrt.length > 0 && (
          <div className="stack tk-anhang-gesperrt">
            <strong>{t("Nicht wählbar", "Not available")}</strong>
            <ul>{gesperrt.map((b, i) => <li key={i}><span>{b.title}</span> <small>{b.reason}</small></li>)}</ul>
          </div>
        )}
      </div>
    </Dialog>
    </>
  );
}
